import { ApolloClient, CombinedGraphQLErrors, InMemoryCache, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApolloProvider as BaseApolloProvider } from '@apollo/client/react';
import { createClient } from 'graphql-ws';
import { useMemo, type ReactNode } from 'react';
import { environment } from '../../environments/environment';
import introspectionResult from '../../introspection-result';

const errorLink = onError(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) => {
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  } else {
    console.error(`[Network error]: ${error}`);
  }
});

export function ApolloProvider({ children }: { children: ReactNode }) {
  const client = useMemo(() => {
    const httpLink = new BatchHttpLink({
      uri: environment.graphql.uri,
    });

    const wsLink = new GraphQLWsLink(
      createClient({
        url: environment.graphql.wsUri,
        connectionParams: () => ({
          authentication: localStorage.getItem('access_token'),
        }),
      }),
    );

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      errorLink.concat(httpLink),
    );

    return new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache({
        possibleTypes: introspectionResult.possibleTypes,
      }),
      defaultOptions: {
        watchQuery: {
          notifyOnNetworkStatusChange: false,
        },
      },
    });
  }, []);

  return <BaseApolloProvider client={client}>{children}</BaseApolloProvider>;
}
